import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'

import axiosClient from '../../api/axiosClient'
import Typography from '../../components/Typography'
import Button from '../../components/Button/Button'
import InputText from '../../components/Input/InputText'
import { theme } from '../../theme'
import {
  formatDate,
  formatEuros,
  INVOICE_STATUS_LABELS
} from './format'

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'issued', label: 'Emitidas' },
  { value: 'draft', label: 'Borradores' },
  { value: 'void', label: 'Anuladas' }
]

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const AdminInvoices = () => {
  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0 })
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    period: '',
    status: '',
    organizerId: ''
  })

  const fetchInvoices = useCallback(async ({ page, pageSize, period, status, organizerId }) => {
    setLoading(true)
    try {
      const params = { page, pageSize }
      if (period) params.period = period
      if (status) params.status = status
      if (organizerId) params.organizerId = organizerId
      const { data } = await axiosClient.get('/api/admin/invoices', { params })
      setItems(data.data.items || [])
      setPagination(data.data.pagination || { page, pageSize, total: 0 })
    } catch (err) {
      console.error('[AdminInvoices] failed to load', err)
      toast.error('No pudimos cargar las facturas.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInvoices({ page: 1, pageSize: pagination.pageSize, ...filters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const applyFilters = (e) => {
    e?.preventDefault?.()
    fetchInvoices({ page: 1, pageSize: pagination.pageSize, ...filters })
  }

  const changePage = (nextPage) => {
    fetchInvoices({ page: nextPage, pageSize: pagination.pageSize, ...filters })
  }

  const changePageSize = (nextSize) => {
    fetchInvoices({ page: 1, pageSize: nextSize, ...filters })
  }

  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.pageSize || 1)))

  const [sweepPeriod, setSweepPeriod] = useState('')
  const [sweeping, setSweeping] = useState(false)

  const triggerSweep = async () => {
    if (!sweepPeriod) return toast.error('Indica un periodo (YYYY-MM).')
    setSweeping(true)
    try {
      const { data } = await axiosClient.post('/api/admin/invoices/trigger-sweep', { invoicePeriod: sweepPeriod })
      const r = data.data
      toast.success(`Sweep ${sweepPeriod}: ${r.emitted} emitidas, ${r.skipped} omitidas.`)
      fetchInvoices({ page: 1, pageSize: pagination.pageSize, ...filters })
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error al ejecutar el sweep.')
    } finally {
      setSweeping(false)
    }
  }

  return (
    <Container>
      <Header>
        <Typography as="h1" $variant="title-4-semibold">Facturas</Typography>
        <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultWeak}>
          Total: {pagination.total} factura{pagination.total === 1 ? '' : 's'}
        </Typography>
      </Header>

      <SweepBar>
        <SweepLabel>Generar facturas del periodo</SweepLabel>
        <InputText
          type="month"
          value={sweepPeriod}
          onChange={(e) => setSweepPeriod(e.target.value)}
          $size="medium"
          style={{ width: 160 }}
        />
        <Button size="sm" onClick={triggerSweep} disabled={sweeping || !sweepPeriod}>
          {sweeping ? 'Generando…' : 'Ejecutar sweep'}
        </Button>
      </SweepBar>

      <Filters onSubmit={applyFilters}>
        <FilterField>
          <Label htmlFor="period">Periodo (YYYY-MM)</Label>
          <InputText
            id="period"
            type="month"
            value={filters.period}
            onChange={(e) => setFilters({ ...filters, period: e.target.value })}
          />
        </FilterField>
        <FilterField>
          <Label htmlFor="status">Estado</Label>
          <Select
            id="status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </FilterField>
        <FilterField>
          <Label htmlFor="organizerId">Organizador (ObjectId)</Label>
          <InputText
            id="organizerId"
            type="text"
            value={filters.organizerId}
            placeholder="65f1a2b3c4d5..."
            onChange={(e) => setFilters({ ...filters, organizerId: e.target.value.trim() })}
          />
        </FilterField>
        <FilterActions>
          <Button type="submit" size="md">Aplicar</Button>
        </FilterActions>
      </Filters>

      {loading ? (
        <Empty><Typography as="p">Cargando…</Typography></Empty>
      ) : items.length === 0 ? (
        <Empty><Typography as="p">No hay facturas con esos filtros.</Typography></Empty>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Número</th>
                <th>Organizador</th>
                <th>Periodo</th>
                <th>Emitida</th>
                <th>Total</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv._id}>
                  <td><Mono>{inv.invoiceNumber}</Mono></td>
                  <td>{inv.recipientSnapshot?.legalName || inv.recipientSnapshot?.email || '—'}</td>
                  <td>{inv.invoicePeriod}</td>
                  <td>{formatDate(inv.issuedAt)}</td>
                  <td>{formatEuros(inv.total, { currency: inv.currency || 'EUR' })}</td>
                  <td><StatusChip $status={inv.status}>{INVOICE_STATUS_LABELS[inv.status] || inv.status}</StatusChip></td>
                  <td>
                    <DetailLink to={`/admin/invoices/${inv._id}`}>Ver</DetailLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Footer>
        <PageSize>
          <Label htmlFor="pageSize">Por página</Label>
          <Select
            id="pageSize"
            value={pagination.pageSize}
            onChange={(e) => changePageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Select>
        </PageSize>
        <Pager>
          <Button size="sm" $variant="outline" onClick={() => changePage(pagination.page - 1)} disabled={pagination.page <= 1 || loading}>
            ← Anterior
          </Button>
          <Typography as="span" $variant="body-2-regular">
            Página {pagination.page} / {totalPages}
          </Typography>
          <Button size="sm" $variant="outline" onClick={() => changePage(pagination.page + 1)} disabled={pagination.page >= totalPages || loading}>
            Siguiente →
          </Button>
        </Pager>
      </Footer>
    </Container>
  )
}

export default AdminInvoices

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Filters = styled.form`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  align-items: end;
  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FilterActions = styled.div`
  display: flex;
  align-items: end;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.defaultStrong};
`

const Select = styled.select`
  height: 40px;
  padding: 0 10px;
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.md};
  background-color: ${theme.fill.defaultMain};
`

const Empty = styled.div`
  padding: 48px 24px;
  border: 1px dashed ${theme.border.defaultMain};
  border-radius: ${theme.radius.md};
  text-align: center;
  color: ${theme.colors.defaultWeak};
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.md};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th, td {
    padding: 12px 14px;
    text-align: left;
    border-bottom: 1px solid ${theme.border.defaultWeak};
  }
  th {
    font-weight: 600;
    color: ${theme.colors.defaultWeak};
    background-color: ${theme.fill.defaultWeak || '#fafafa'};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover { background-color: ${theme.fill.defaultWeak || '#fafafa'}; }
`

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
`

const StatusChip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ $status }) => {
    if ($status === 'issued') return 'background-color: #d6efd5; color: #1b5e20;'
    if ($status === 'void') return 'background-color: #f9d6d6; color: #b71c1c;'
    return 'background-color: #fff3cd; color: #856404;'
  }}
`

const DetailLink = styled(Link)`
  color: ${theme.colors.brandMain || '#ff5722'};
  text-decoration: none;
  font-weight: 600;
  font-size: 13px;
  &:hover { text-decoration: underline; }
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const PageSize = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${Label} { margin: 0; }
`

const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SweepBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: ${theme.fill.defaultWeak};
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.md};
`

const SweepLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.defaultStrong};
  white-space: nowrap;
`
